import Icon from "@expo/vector-icons/MaterialCommunityIcons";
import { useAppTheme } from "@hooks/useAppTheme";
import { useDevice } from "@hooks/useDevice";
import { Box } from "@memorang/ui";
import { ModuleActionButton } from "components/ModuleActionButton";
import { ProgressBar, Text, TouchableRipple } from "react-native-paper";
import type { EXAM_STATUS } from "types/tag";
import { buttonConfig } from "../../constants";
import type { ProethicaScoreRowData } from "../../helpers/proethica-progress-data";
import { getScoreBandColor } from "../../helpers/proethica-progress-data";
import { ModuleThumbnail } from "../ModuleThumbnail";

type Props = ProethicaScoreRowData & {
	onPress?: () => void;
	loading?: boolean;
};

const ctaMode = (status: EXAM_STATUS) =>
	buttonConfig[status]?.buttonMode ?? "contained";
const ctaIcon = (status: EXAM_STATUS) =>
	buttonConfig[status]?.buttonIcon ?? "play";

export const ProethicaScoreRow = ({
	title,
	status,
	disabled,
	cta,
	scorePercent,
	thumbnailUrl,
	thumbnailSource,
	isTest,
	testKind,
	icon,
	onPress,
	loading,
}: Props) => {
	const theme = useAppTheme();
	const { isMobile } = useDevice();

	const showScore = status === "COMPLETE" && scorePercent != null;
	const scoreColor = showScore
		? getScoreBandColor(scorePercent, theme)
		: theme.colors.onSurfaceVariant;

	const trailing = showScore ? null : disabled ? (
		<Box flexDirection="row" alignItems="center" style={{ gap: 4 }}>
			<Icon
				name="lock-outline"
				size={14}
				color={theme.colors.onSurfaceVariant}
			/>
			<Text
				variant="labelMedium"
				style={{ color: theme.colors.onSurfaceVariant }}
			>
				Locked
			</Text>
		</Box>
	) : (
		<ModuleActionButton
			cta={cta}
			mode={ctaMode(status)}
			icon={ctaIcon(status)}
			loading={loading}
			compact={isMobile}
			onPress={() => onPress?.()}
		/>
	);

	const detail = showScore ? (
		<Box flexDirection="row" alignItems="center" style={{ gap: 12 }}>
			<Box flex={1}>
				<ProgressBar
					progress={Math.min(1, Math.max(0, scorePercent / 100))}
					color={scoreColor}
					style={{
						height: 6,
						borderRadius: 999,
						backgroundColor: theme.colors.progress.none,
					}}
				/>
			</Box>
			<Text
				variant="titleSmall"
				style={{
					color: scoreColor,
					fontWeight: "700",
					minWidth: 36,
					textAlign: "right",
				}}
			>
				{scorePercent}%
			</Text>
		</Box>
	) : disabled ? (
		<Text variant="bodySmall" style={{ color: theme.colors.onSurfaceVariant }}>
			Unlocks after the previous module
		</Text>
	) : null;

	const rowBody = (
		<Box
			flexDirection="row"
			alignItems="center"
			paddingVertical={14}
			paddingHorizontal={16}
			style={{ gap: 12 }}
		>
			<ModuleThumbnail
				size={44}
				title={title}
				thumbnailUrl={thumbnailUrl}
				thumbnailSource={thumbnailSource}
				isTest={isTest}
				testKind={testKind}
				icon={icon}
				locked={disabled}
			/>

			<Box flex={1} style={{ gap: 8 }}>
				<Box flexDirection="row" alignItems="center" style={{ gap: 8 }}>
					<Text
						variant="titleSmall"
						style={{
							flex: 1,
							color: theme.colors.onSurface,
							fontWeight: "600",
						}}
						numberOfLines={1}
					>
						{title}
					</Text>

					{trailing}
				</Box>

				{detail}
			</Box>
		</Box>
	);

	if (onPress && !disabled) {
		return (
			<TouchableRipple onPress={onPress} accessibilityRole="button">
				{rowBody}
			</TouchableRipple>
		);
	}

	return rowBody;
};
